import { useState, useRef, useEffect, useMemo } from "react";
import { countryCodes, type CountryCode } from "@/mocks/countryCodes";

interface CountryCodePickerProps {
  value: CountryCode;
  onChange: (country: CountryCode) => void;
  variant?: "light" | "dark";
}

export default function CountryCodePicker({
  value,
  onChange,
  variant = "light",
}: CountryCodePickerProps) {
  const { i18n } = useTranslation();
  const isAr = i18n.language?.startsWith("ar");
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const filtered = useMemo(() => {
    if (!search.trim()) return countryCodes;
    const q = search.toLowerCase().trim();
    return countryCodes.filter(
      (c) =>
        c.country.toLowerCase().includes(q) ||
        c.countryAr.includes(q) ||
        c.code.includes(q)
    );
  }, [search]);

  const displayName = isAr ? value.countryAr : value.country;

  const triggerClasses =
    variant === "dark"
      ? "text-white/90 bg-white/10 border-white/20 hover:bg-white/15"
      : "text-foreground-700 bg-background-100 border-background-200 hover:border-primary-300";

  return (
    <div ref={ref} className="relative flex-shrink-0">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-2 px-3 py-3 border rounded-xl text-sm transition-colors cursor-pointer whitespace-nowrap ${triggerClasses}`}
      >
        <span className="text-lg leading-none">{value.flag}</span>
        <span className="text-xs font-medium text-foreground-500">
          {value.code}
        </span>
        {open ? (
          <i className="ri-arrow-up-s-line text-xs text-foreground-400"></i>
        ) : (
          <i className="ri-arrow-down-s-line text-xs text-foreground-400"></i>
        )}
      </button>

      {open && (
        <div
          className={`absolute top-full mt-2 ${
            isAr ? "right-0" : "left-0"
          } w-72 bg-background-50 border border-background-200 rounded-xl z-50 overflow-hidden`}
        >
          {/* Search */}
          <div className="p-3 border-b border-background-200">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={isAr ? "بحث عن دولة..." : "Search country..."}
                className="w-full pl-9 pr-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
              />
            </div>
          </div>

          {/* Country list */}
          <div className="max-h-64 overflow-y-auto p-2">
            {filtered.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-foreground-400">
                {isAr ? "لا توجد نتائج" : "No results found"}
              </div>
            ) : (
              filtered.map((country) => {
                const name = isAr ? country.countryAr : country.country;
                const isSelected = country.code === value.code && country.country === value.country;
                return (
                  <button
                    key={`${country.code}-${country.country}`}
                    type="button"
                    onClick={() => {
                      onChange(country);
                      setOpen(false);
                      setSearch("");
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors cursor-pointer ${
                      isSelected
                        ? "bg-primary-50 text-primary-700"
                        : "text-foreground-700 hover:bg-background-100"
                    }`}
                  >
                    <span className="text-xl leading-none">{country.flag}</span>
                    <span className="flex-1 text-left whitespace-nowrap">
                      {name}
                    </span>
                    <span className="text-xs text-foreground-400">
                      {country.code}
                    </span>
                    {isSelected && (
                      <i className="ri-check-line text-primary-500 text-sm"></i>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}